import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../i18n/en.json';
import de from '../i18n/de.json';
import gr from '../i18n/gr.json';

const LanguageContext = createContext();

const translations = {
  en,
  de,
  gr
};

/**
 * Language Provider Component
 * Wraps the app and provides language context
 */
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'gr'
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'gr';
  });

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const t = (path) => {
    const keys = path.split('.');

    // Helper: walk the translation object along the key path
    const resolve = (obj) => {
      let value = obj;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return undefined;
        }
      }
      return (value !== undefined && value !== null && value !== '') ? value : undefined;
    };

    // 1. Try current language
    const primary = resolve(translations[language]);
    if (primary !== undefined) return primary;

    // 2. Fall back to English
    const fallback = resolve(translations['en']);
    if (fallback !== undefined) return fallback;

    // 3. Last resort: return the key path itself
    return path;
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 * @returns {Object} Language context with language, changeLanguage, and t function
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
